import { ui_service_ip } from "./ip_details";
import { call_post_api, call_get_api, call_delete_api, call_update_api } from "./event_handling";
/**
 * Generic function to handle API calls based on the method type.
 * @param {string} method - The HTTP method (GET, POST, UPDATE, DELETE).
 * @param {string} endpoint - The endpoint for the API call.
 * @param {Object} params - The parameters or request body for the API call.
 * @returns {Object} - The result from the API call.
 */
async function handleApiCall(method, endpoint, params) {
    try {
        let result;
        switch (method.toUpperCase()) {
            case "GET":
                result = await call_get_api(ui_service_ip, endpoint, params);
                break;

            case "POST":
                result = await call_post_api(ui_service_ip, endpoint, params);
                break;

            case "PUT":
            case "UPDATE":
                result = await call_update_api(ui_service_ip, endpoint, params);
                break;

            case "DELETE":
                result = await call_delete_api(ui_service_ip, endpoint, params);
                break;

            default:
                throw new Error(`Unsupported HTTP method: ${method}`);
        }

        return result; // Return the result from the API
    } catch (error) {
        console.error(`Error during ${method} request to ${endpoint}:`, error); // Log any errors
    }
}

/**
 * Function to fetch the latest environment list (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getLatestEnvironmentList(requestParams) {
    return handleApiCall("GET", "get_environments_latest", requestParams);
}

/**
 * Function to fetch the watchlist (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getWatchList(requestParams) {
    return handleApiCall("GET", "get-watchlist", requestParams);
}

/**
 * Function to fetch all environment integrations (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getAllEnvironmentIntegrations(requestParams) {
    return handleApiCall("GET", "env/list-integrations", requestParams);
}

/**
 * Function to fetch metrics value from the real-time monitoring tree (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getMetricsValue(requestParams) {
    
    return handleApiCall("GET", "monitor/rt_tree", requestParams);
}

/**
 * Function to fetch chart data for real-time monitoring (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getChartData(requestParams) {
    return handleApiCall("GET", "monitor/realtime", requestParams);
}

/**
 * Function to fetch all metadata mappings (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getAllMetaData(requestParams) {
    return handleApiCall("GET", "metadata_mapping", requestParams);
}

/**
 * Function to save data to the API (POST example).
 * @param {Object} requestBody - The request body for the API call.
 * @returns {Object} - The result from the API call.
 */
export function saveWatchList(requestBody) {
    return handleApiCall("POST", "save-watchlist", requestBody);
}

/**
 * Function to delete data from the API (DELETE example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function deleteWatchList(requestParams) {
    return handleApiCall("DELETE", "delete-watchlist", requestParams);
}

/**
 * Function to edit the watchlist (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getEditWatchList(requestParams) {
    return handleApiCall("GET", "edit-watchlist", requestParams);
}

/**
 * Function to update data in the API (PUT/UPDATE example).
 * @param {Object} requestBody - The request body for the API call.
 * @returns {Object} - The result from the API call.
 */
export function updateWatchList(requestBody) {
    return handleApiCall("UPDATE", "edit-watchlist", requestBody);
}

/**
 * Function to delete data from the API (DELETE example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function deleteUserTracking(requestParams) {
    return handleApiCall("DELETE", "user_tracking", requestParams);
}

/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function addUserTracking(requestParams) {
    return handleApiCall("POST", "user_tracking", requestParams);
}

/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getIncidents(requestParams) {
    return handleApiCall("GET", "incidents", requestParams);
}


/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getWebSocketUrl(requestParams) {
    return handleApiCall("GET", "get_ws_string", requestParams);
}

/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getIncidentSummary(requestParams) {
    return handleApiCall("GET", "get_incident_summary_data", requestParams);
}

/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getIncidentRecurring(requestParams) {
    return handleApiCall("GET", "incidents/recurring", requestParams);
}



/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getAllIncidents(requestParams) {
    return handleApiCall("GET", "incidents/all_incidents", requestParams);
}


/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getGlanceOfIncident(requestParams) {
    return handleApiCall("GET", "incidents/glance_of_incidents", requestParams);
}

/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function postIncident(requestParams) {
    return handleApiCall("POST", "incidents/interactions", requestParams);
}

/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function allIncidentFilterGrid(requestParams, issue_count) {
    const topIssues = issue_count === undefined ? requestParams.top_issues : issue_count;
    const normalizedTopIssues = topIssues === "All"
        ? topIssues
        : Number.isFinite(Number(topIssues))
            ? Number(topIssues)
            : topIssues;

    return handleApiCall("GET", "filter/incidents", {
        ...requestParams,
        top_issues: normalizedTopIssues,
    });
}

export function searchIncidents(requestParams) {
    return handleApiCall("POST", "incidents/search_incidents", requestParams);
}

// /incidents/search_incidents
//Input : {"data": {"pipeline_incident_id": 16, "liked": False, "ignored": True, "read": True}} or {"data": {"dataquality_incident_id": 41, "liked": True, "ignored": False, "read": True}}

